package se.mittpass.web.components

import se.mittpass.shared.api.StaffMemberDto

/**
 * Designated color scheme for an employee.
 * [primary]: Solid color used for badges, avatar borders, and strong indicators.
 * [bg]: Soft pastel tint (~12-15% opacity or soft pastel) for card backgrounds.
 * [text]: High-contrast dark shade corresponding to the hue for readable text.
 * [border]: Subtle border color framing the element.
 */
data class EmployeeColorScheme(
    val primary: String,
    val bg: String,
    val text: String,
    val border: String
)

val DEFAULT_PALETTE_COLORS = listOf(
    "#4F46E5", // Indigo
    "#059669", // Emerald
    "#D97706", // Amber
    "#DC2626", // Red
    "#7C3AED", // Violet
    "#0284C7", // Sky Blue
    "#DB2777", // Pink
    "#D946EF", // Fuchsia
    "#0D9488", // Teal
    "#EA580C", // Orange
    "#475569", // Slate
    "#15803D"  // Forest Green
)

val COLOR_SCHEMES: Map<String, EmployeeColorScheme> = mapOf(
    "#4F46E5" to EmployeeColorScheme("#4F46E5", "color-mix(in srgb, #4F46E5 14%, var(--rp-surface))", "var(--rp-text)", "color-mix(in srgb, #4F46E5 35%, var(--rp-surface))"),
    "#059669" to EmployeeColorScheme("#059669", "color-mix(in srgb, #059669 14%, var(--rp-surface))", "var(--rp-text)", "color-mix(in srgb, #059669 35%, var(--rp-surface))"),
    "#D97706" to EmployeeColorScheme("#D97706", "color-mix(in srgb, #D97706 14%, var(--rp-surface))", "var(--rp-text)", "color-mix(in srgb, #D97706 35%, var(--rp-surface))"),
    "#DC2626" to EmployeeColorScheme("#DC2626", "color-mix(in srgb, #DC2626 14%, var(--rp-surface))", "var(--rp-text)", "color-mix(in srgb, #DC2626 35%, var(--rp-surface))"),
    "#7C3AED" to EmployeeColorScheme("#7C3AED", "color-mix(in srgb, #7C3AED 14%, var(--rp-surface))", "var(--rp-text)", "color-mix(in srgb, #7C3AED 35%, var(--rp-surface))"),
    "#0284C7" to EmployeeColorScheme("#0284C7", "color-mix(in srgb, #0284C7 14%, var(--rp-surface))", "var(--rp-text)", "color-mix(in srgb, #0284C7 35%, var(--rp-surface))"),
    "#DB2777" to EmployeeColorScheme("#DB2777", "color-mix(in srgb, #DB2777 14%, var(--rp-surface))", "var(--rp-text)", "color-mix(in srgb, #DB2777 35%, var(--rp-surface))"),
    "#D946EF" to EmployeeColorScheme("#D946EF", "color-mix(in srgb, #D946EF 14%, var(--rp-surface))", "var(--rp-text)", "color-mix(in srgb, #D946EF 35%, var(--rp-surface))"),
    "#0D9488" to EmployeeColorScheme("#0D9488", "color-mix(in srgb, #0D9488 14%, var(--rp-surface))", "var(--rp-text)", "color-mix(in srgb, #0D9488 35%, var(--rp-surface))"),
    "#EA580C" to EmployeeColorScheme("#EA580C", "color-mix(in srgb, #EA580C 14%, var(--rp-surface))", "var(--rp-text)", "color-mix(in srgb, #EA580C 35%, var(--rp-surface))"),
    "#475569" to EmployeeColorScheme("#475569", "color-mix(in srgb, #475569 14%, var(--rp-surface))", "var(--rp-text)", "color-mix(in srgb, #475569 35%, var(--rp-surface))"),
    "#15803D" to EmployeeColorScheme("#15803D", "color-mix(in srgb, #15803D 14%, var(--rp-surface))", "var(--rp-text)", "color-mix(in srgb, #15803D 35%, var(--rp-surface))")
)

val DEFAULT_COLOR_SCHEME = EmployeeColorScheme(
    primary = "var(--rp-pine)",
    bg = "color-mix(in srgb, var(--rp-pine) 14%, var(--rp-surface))",
    text = "var(--rp-text)",
    border = "color-mix(in srgb, var(--rp-pine) 35%, var(--rp-surface))"
)

/**
 * Returns the color scheme for an employee based on their designated color or fallback to user ID / default.
 */
fun getEmployeeColorScheme(color: String?, userId: Long? = null): EmployeeColorScheme {
    if (!color.isNullOrBlank()) {
        val normalized = color.trim().uppercase()
        val match = COLOR_SCHEMES.entries.find { it.key.equals(normalized, ignoreCase = true) }
        if (match != null) return match.value
        // If it's a custom hex color not directly in predefined palette
        return EmployeeColorScheme(
            primary = color,
            bg = "color-mix(in srgb, $color 14%, var(--rp-surface))",
            text = "var(--rp-text)",
            border = "color-mix(in srgb, $color 35%, var(--rp-surface))"
        )
    }

    if (userId != null && userId > 0) {
        val idx = (userId % DEFAULT_PALETTE_COLORS.size).toInt()
        val defaultHex = DEFAULT_PALETTE_COLORS[idx]
        return COLOR_SCHEMES[defaultHex] ?: DEFAULT_COLOR_SCHEME
    }

    return DEFAULT_COLOR_SCHEME
}

/**
 * Helper to get the employee color scheme directly from the staff list given an assigned user ID.
 */
fun getStaffColorScheme(staff: List<StaffMemberDto>, assignedUserId: Long?): EmployeeColorScheme {
    val member = staff.find { it.userId == assignedUserId }
    return getEmployeeColorScheme(member?.color, assignedUserId)
}
